import { Big } from 'big.js';
import { addressToFull, DisplayData, TenantOrderLine } from '~/lib/model';
import BrePhotosConfig from '~/tenants/bre/performable/photos/BrePhotosConfig';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderContext, BreOrderMetadata } from '~/tenants/bre/model/BreOrderContext';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { BrePhotosRate } from '~/tenants/bre/performable/photos/BrePhotosData';
import { BRE_COPYRIGHT_PHOTO_FEE, bre_version, BreVersion } from '~/tenants/bre/data';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { DateTime } from 'luxon';
import {
  bre_increase_commercial,
  bre_increase_residential,
  bre_round,
  bre_split_photo,
} from '~/tenants/bre/performable/common';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { DeliverableType } from '~common/model/Deliverable';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';

interface PhotosRateOptions {
  context: BreOrderContext;
  photos: number;
  created?: TDateISO | DateTime;
}

export default class BrePhotosJob extends BreJob<typeof BrePhotosConfig> {
  get performable(): typeof BrePhotosConfig {
    return BrePhotosConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get submission() {
    return true;
  }

  get media() {
    if (this.uploading) {
      return PersonaType.PROVIDER;
    }

    return null;
  }

  get deliverable() {
    if (this.uploading) {
      return DeliverableType.PHOTO;
    }

    return null;
  }

  get editor() {
    return this.editing ? BreProviderId.NIGHT_LEO.toString() : null;
  }

  get uploading() {
    return false;
    // if (this.appointment && bre_version(this.appointment.start, BreVersion.V2)) {
    //   return true;
    // }
    //
    // return bre_version(this.order.created, BreVersion.V2);
  }

  get editing() {
    return this.uploading;
  }

  /**
   * The photographer's per-job "AI edit" choice, AND the company's editing
   * switch (the staff kill switch, which also gates the charge): the box
   * alone used to send a job to an engine with editing switched off.
   */
  get autoenhance() {
    return this.order.editing?.enabled === true && Boolean(this.metadata.ai_edit);
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.RENTAL, BreOrderType.LONG_TERM);
  }

  info(): DisplayData[] {
    const info = super.info();

    if (this.metadata.photos > 0) {
      info.push({
        name: 'Photos',
        provider: true,
        value: this.metadata.photos.toString(),
      });
    }

    info.push({
      name: 'Lighting',
      provider: true,
      value: this.metadata.twilight ? 'Twilight' : 'Daylight',
    });

    if (this.metadata.rush) {
      info.push({
        name: 'Rush',
        provider: true,
        value: 'Yes',
      });
    }

    if (this.metadata.local_area && this.metadata.local_area.length > 0) {
      info.push({
        name: 'Local Area Photos',
        provider: true,
        value: this.metadata.local_area.map((l) => `${l.name}:${addressToFull(l.address)}`).join(', '),
      });
    }

    if (this.metadata.max_extra_photos && this.metadata.max_extra_photos > 0) {
      info.push({
        name: 'Max Extra Photos',
        provider: true,
        value: this.metadata.max_extra_photos.toString(),
      });
    }

    return info;
  }

  get copyrightFee(): Big {
    const { photos } = this.metadata;
    const { type } = this.order.metadata;

    return new Big(BRE_COPYRIGHT_PHOTO_FEE[type]).times(photos);
  }

  static recommendedPhotos(metadata: BreOrderMetadata): number {
    const { type } = metadata;

    if (type !== BreOrderType.AGENT_MKTG && type !== BreOrderType.PROPERTY_MKTG && metadata.sqft >= 1500) {
      const closest = Math.floor((metadata.sqft - 500) / 1000) * 1000 + 500;
      return closest / 100;
    }

    return 15;
  }

  static calculateIncreasedRate(options: PhotosRateOptions): Big {
    return bre_round(bre_increase_residential(BrePhotosJob.calculateOriginalRate(options)));
  }

  static calculateOriginalRate({ context, photos }: PhotosRateOptions): Big {
    const original = new Big('6.00').times(photos - 15).add(200);

    if (context.metadata.type === BreOrderType.COMMERCIAL) {
      return bre_increase_commercial(original);
    }

    return original;
  }

  defaultValue(): ZodVersionedMetadata<(typeof BrePhotosConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      photos: BrePhotosJob.recommendedPhotos(this.order.metadata),
    };
  }

  onsite(): number {
    const { photos, twilight, local_area } = this.metadata;
    const photoTime = photos + 25;
    const twilightTime = twilight ? 40 : 0;
    const localAreaTime = local_area ? local_area.length * 20 : 0;

    return photoTime + twilightTime + localAreaTime;
  }

  revenueLines(): TenantOrderLine[] {
    const { photos, community, local_area, rush, twilight } = this.metadata;
    const { COMMUNITY, LOCAL_AREA, RUSH, TWILIGHT } = BrePhotosRate[this.order.metadata.type];
    const hasCommunity = this.order.jobs.some((j) => j.performable_id === 'community');
    const lines: TenantOrderLine[] = [];

    const basePhotos = BrePhotosJob.calculateIncreasedRate({
      context: this.order,
      photos: photos,
      created: this.orderCreated,
    });

    if (this.metadata.extra_photos && this.metadata.extra_photos > 0) {
      lines.push({
        id: 'extra_photos',
        description: `${this.metadata.extra_photos} Extra Photos`,
        amount: BrePhotosJob.calculateIncreasedRate({
          context: this.order,
          photos: photos + this.metadata.extra_photos,
          created: this.orderCreated,
        }).minus(basePhotos),
      });
    }

    if (photos > 0) {
      lines.push({
        id: 'photos',
        description: `${photos} Daytime Photos`,
        amount: basePhotos,
        discountable: true,
      });

      if (this.isOneOf(BreOrderType.LONG_TERM)) {
        lines.push({
          id: 'copyright',
          description: 'Copyright',
          amount: this.copyrightFee,
        });
      }
    }

    if (twilight) {
      lines.push({
        id: 'twilight',
        description: '8 Twilight Photos',
        amount: new Big(TWILIGHT),
      });
    }

    if (community && community.length > 0 && !hasCommunity) {
      lines.push({
        id: 'community_photos',
        description: `Community Photos (x${community.length})`,
        amount: new Big(COMMUNITY).times(community.length),
      });
    }

    if (local_area && local_area.length > 0) {
      lines.push({
        id: 'poi',
        description: `Local Area Photos (x${local_area.length})`,
        amount: new Big(LOCAL_AREA).times(local_area.length),
      });
    }

    if (rush) {
      lines.push({
        id: 'rush_fee',
        description: 'Before noon rush fee',
        amount: new Big(RUSH),
      });
    }

    return lines;
  }

  expenseLines(): TenantOrderLine[] {
    const { photos, local_area, twilight } = this.metadata;
    const { LOCAL_AREA, TWILIGHT } = BrePhotosRate[this.order.metadata.type];
    const lines: TenantOrderLine[] = [];

    const basePhotos = BrePhotosJob.calculateOriginalRate({
      context: this.order,
      photos: photos,
      created: this.orderCreated,
    });

    if (this.metadata.extra_photos && this.metadata.extra_photos > 0) {
      lines.push({
        id: 'extra_photos',
        description: `${this.metadata.extra_photos} Extra Photos`,
        amount: bre_split_photo(
          BrePhotosJob.calculateOriginalRate({
            context: this.order,
            photos: photos + this.metadata.extra_photos,
            created: this.orderCreated,
          }).minus(basePhotos),
        ),
      });
    }

    if (photos > 0) {
      lines.push({
        id: 'photos',
        description: `${photos} Daytime Photos`,
        amount: bre_split_photo(basePhotos),
      });
    }

    if (this.metadata.editing_credits && this.metadata.editing_credits > 0) {
      lines.push({
        id: 'editing_credits',
        description: 'Editing Credits',
        amount: new Big(this.metadata.editing_credits).times('-1'),
      });
    }

    if (twilight) {
      lines.push({
        id: 'twilight',
        description: '8 Twilight Photos',
        amount: new Big(TWILIGHT).div('1.05').times('0.65'),
      });
    }

    if (local_area && local_area.length > 0) {
      lines.push({
        id: 'poi',
        description: `Local Area Photos (x${local_area.length})`,
        amount: new Big(LOCAL_AREA).times(local_area.length).times('0.65'),
      });
    }

    return lines;
  }

  get minimumPhotos(): number {
    return this.metadata.photos;
  }
}
